import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAuth } from '../../context/AuthContext';
import { createAccolade, ACCOLADE_CATEGORIES, ACCOLADE_RARITIES } from '../../services/accoladeService';
import { getAllCharacters, uploadImage } from '../../services/characterService';
import { getAllCampaignEpisodes } from '../../services/campaignEpisodeService';

const CreateAccoladeModal = ({ open, onClose, onAccoladeCreated }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    characterId: '',
    characterName: '',
    category: '',
    rarity: 'Bronze',
    campaignEpisodeId: '',
    campaignEpisodeTitle: '',
    imageUrl: ''
  });
  const [characters, setCharacters] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      // Fetch characters for character recipients
      const allCharacters = await getAllCharacters();
      setCharacters(allCharacters);
      
      // Fetch users for player recipients (you might need to implement this)
      // const allUsers = await getAllUsers(); // This would need to be implemented
      // setUsers(allUsers);
      
      // Fetch campaign episodes for reference
      const allEpisodes = await getAllCampaignEpisodes();
      setEpisodes(allEpisodes);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data for the form.');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleCharacterChange = (characterId) => {
    const character = characters.find(char => char.id === characterId);
    
    if (character) {
      setFormData(prev => ({
        ...prev,
        characterId,
        characterName: character.name
      }));
    }
  };

  const handleEpisodeChange = (episodeId) => {
    const episode = episodes.find(ep => ep.id === episodeId);
    if (episode) {
      setFormData(prev => ({
        ...prev,
        campaignEpisodeId: episodeId,
        campaignEpisodeTitle: episode.title
      }));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image file size must be less than 5MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageFile = async () => {
    if (!imageFile) return '';
    
    setUploadingImage(true);
    try {
      // You'll need to implement image upload service
      const imageUrl = await uploadImage(imageFile, 'accolades');
      return imageUrl;
    } catch (err) {
      console.error('Error uploading image:', err);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.characterId || !formData.category) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let imageUrl = '';
      if (imageFile) {
        imageUrl = await uploadImageFile();
      }

      const accoladeData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        characterId: formData.characterId,
        characterName: formData.characterName,
        category: formData.category,
        rarity: formData.rarity,
        awardedByUserId: currentUser.uid,
        awardedByUserName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown',
        ...(imageUrl && { imageUrl }),
        ...(formData.campaignEpisodeId && {
          campaignEpisodeId: formData.campaignEpisodeId,
          campaignEpisodeTitle: formData.campaignEpisodeTitle
        })
      };

      await createAccolade(accoladeData);
      onAccoladeCreated();
      resetForm();
    } catch (err) {
      console.error('Error creating accolade:', err);
      setError(err.message || 'Failed to create accolade. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      characterId: '',
      characterName: '',
      category: '',
      rarity: 'Bronze',
      campaignEpisodeId: '',
      campaignEpisodeTitle: '',
      imageUrl: ''
    });
    setImageFile(null);
    setImagePreview('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getRarityIcon = (rarity) => {
    switch (rarity) {
      case 'Bronze': return '🥉';
      case 'Silver': return '🥈';
      case 'Gold': return '🥇';
      case 'Legendary': return '👑';
      default: return '🏆';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Combat Excellence': '⚔️',
      'Roleplay': '🎭',
      'Problem Solving': '🧩',
      'Leadership': '👑',
      'Creativity': '🎨',
      'Team Player': '🤝',
      'Character Development': '📈',
      'Memorable Moment': '✨',
      'Strategic Thinking': '🧠',
      'Humor': '😂',
      'Heroic Deed': '🦸',
      'Other': '🏆'
    };
    return icons[category] || '🏆';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2c2c2c',
          color: '#F5F5F5',
          border: '1px solid #d98d2a',
          minHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#3c3c3c', 
        color: '#d98d2a',
        fontFamily: '"Georgia", serif',
        fontSize: '1.8rem',
        fontWeight: 'bold',
        py: 3,
        borderBottom: '1px solid #555'
      }}>
        🏆 Award New Accolade
      </DialogTitle>
      
      <DialogContent sx={{ pt: 4, pb: 2, px: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: '#ff6b6b', color: 'white' }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Title */}
            <TextField
              label="Accolade Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              fullWidth
              required
              placeholder="e.g., 'Heroic Last Stand', 'Master Negotiator'"
              InputLabelProps={{ style: { color: '#d98d2a' } }}
              inputProps={{ style: { color: '#F5F5F5', fontFamily: '"Georgia", serif' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d98d2a' },
                  '&:hover fieldset': { borderColor: '#c87d26' },
                  '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
                }
              }}
            />

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Describe the achievement or memorable moment..."
              InputLabelProps={{ style: { color: '#d98d2a' } }}
              inputProps={{ style: { color: '#F5F5F5', fontFamily: '"Georgia", serif' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d98d2a' },
                  '&:hover fieldset': { borderColor: '#c87d26' },
                  '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
                }
              }}
            />

            {/* Character Selection */}
            <FormControl fullWidth required>
              <InputLabel sx={{ color: '#d98d2a' }}>Character</InputLabel>
              <Select
                value={formData.characterId}
                onChange={(e) => handleCharacterChange(e.target.value)}
                label="Character"
                sx={{
                  color: '#F5F5F5',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                  '& .MuiSvgIcon-root': { color: '#d98d2a' }
                }}
              >
                {characters.map((character) => (
                  <MenuItem key={character.id} value={character.id}>
                    {character.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Category and Rarity Row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel sx={{ color: '#d98d2a' }}>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                  sx={{
                    color: '#F5F5F5',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                    '& .MuiSvgIcon-root': { color: '#d98d2a' }
                  }}
                >
                  {ACCOLADE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {getCategoryIcon(category)} {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel sx={{ color: '#d98d2a' }}>Rarity</InputLabel>
                <Select
                  value={formData.rarity}
                  onChange={(e) => handleInputChange('rarity', e.target.value)}
                  label="Rarity"
                  sx={{
                    color: '#F5F5F5',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                    '& .MuiSvgIcon-root': { color: '#d98d2a' }
                  }}
                >
                  {ACCOLADE_RARITIES.map((rarity) => (
                    <MenuItem key={rarity} value={rarity}>
                      {getRarityIcon(rarity)} {rarity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Campaign Episode (Optional) */}
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#d98d2a' }}>Campaign Episode (Optional)</InputLabel>
              <Select
                value={formData.campaignEpisodeId}
                onChange={(e) => handleEpisodeChange(e.target.value)}
                label="Campaign Episode (Optional)"
                sx={{
                  color: '#F5F5F5',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                  '& .MuiSvgIcon-root': { color: '#d98d2a' }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {episodes.map((episode) => (
                  <MenuItem key={episode.id} value={episode.id}>
                    {episode.episodeNumber ? `Episode ${episode.episodeNumber}: ` : ''}{episode.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Image Upload */}
            <Paper sx={{ p: 3, backgroundColor: '#3c3c3c', border: '1px solid #555' }}>
              <Typography variant="h6" sx={{ color: '#d98d2a', mb: 2, fontFamily: '"Georgia", serif' }}>
                Accolade Image (Optional)
              </Typography>
              
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  color: '#d98d2a',
                  borderColor: '#d98d2a',
                  mb: 2,
                  '&:hover': { borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)' }
                }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
              
              {imagePreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '200px', 
                      borderRadius: '8px',
                      border: '1px solid #555'
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 4, backgroundColor: '#3c3c3c', borderTop: '1px solid #555' }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          sx={{ color: '#A9A9A9', fontFamily: '"Georgia", serif' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || uploadingImage}
          variant="contained"
          sx={{
            backgroundColor: '#d98d2a',
            color: '#1c1c1c',
            fontFamily: '"Georgia", serif',
            '&:hover': { backgroundColor: '#c87d26' },
            '&:disabled': { backgroundColor: '#666' }
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#1c1c1c' }} />
              Creating...
            </>
          ) : uploadingImage ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#1c1c1c' }} />
              Uploading...
            </>
          ) : (
            'Award Accolade'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAccoladeModal;
